import { Controller } from 'stimulus'
import { throttle, debounce } from 'throttle-debounce'
import { calculateAscenderHeightRatio } from '~/common/helpers/font'
import { parseTextDecorationConfig } from '~/lib/text_decoration_config'
import './base_controller.css'

const SVG_NS = 'http://www.w3.org/2000/svg'

export default class TextDecorationBaseController extends Controller {
  initialize() {
    this.decoratorsContainer = document.createElement('span')
    this.decoratorsContainer.classList.add(
      'text-decoration-decorators-container'
    )

    this.decoratorContainer = document.createElement('span')
    this.decoratorContainer.classList.add('text-decoration-decorator-container')
  }

  connect() {
    this.initializeReferenceElement()

    this.referenceElement.insertAdjacentElement(
      'beforeend',
      this.decoratorsContainer
    )

    // The decoration dropdown uses this to position itself correctly.
    this.element.decoratorsContainer = this.decoratorsContainer

    this.drawDecorators()
    this.resizeObserver = new ResizeObserver(() => {
      this.drawDecorators()
    })
    setTimeout(
      () =>
        this.resizeObserver?.observe(
          this.getClosestNonInlineAncestor(this.element)
        ),
      10
    )
    this.tiptapEditor?.addEventListener?.(
      'wysiwyg-changed',
      this.drawDecorators
    )
    this.builderNode?.addEventListener?.(
      'builder--animation:animation-completed',
      this.drawDecorators
    )
  }

  disconnect() {
    this.tiptapEditor?.removeEventListener?.(
      'wysiwyg-changed',
      this.drawDecorators
    )
    this.builderNode?.removeEventListener?.(
      'builder--animation:animation-completed',
      this.drawDecorators
    )
    this.decoratorsContainer.remove()
    this.resizeObserver.disconnect()
    this.resizeObserver = null
  }

  drawDecorator(_rect) {
    throw new Error('drawDecorator must be implemented')
  }

  // Override in subclasses when necessary
  useAscenderHeight() {
    return true
  }

  animationConfig() {
    return {
      animation: this.config.animation,
      animationDuration: this.config.animationDuration,
      animationType: this.config.animationType,
    }
  }

  drawDecorators = throttle(20, (_event) => {
    const newConfig =
      parseTextDecorationConfig(this.element.dataset.textDecorationConfig) || {}
    const newPositions = this.computeDecoratorPositions()
    if (newPositions.length < 1) return

    if (
      this.config &&
      this.positions &&
      JSON.stringify(this.config) === JSON.stringify(newConfig) &&
      JSON.stringify(this.positions) === JSON.stringify(newPositions)
    ) {
      return // Nothing changed
    }

    this.config = newConfig
    this.positions = newPositions

    this.strokeColor = this.config.strokeColor || 'currentColor'

    this.strokeWidthPixel = Math.max(
      getStrokeWidthPixel(
        this.element,
        this.config.strokeWidth,
        this.config.strokeWidthUnit
      ),
      1
    )

    this.decoratorsContainer.innerHTML = ''

    this.decoratorsContainer.classList.toggle(
      'bring-to-front',
      this.config.bringToFront
    )

    this.setupAnimation()

    newPositions.forEach((rect) => {
      const { top, left, width, height } = rect
      const decoratorContainer = this.decoratorContainer.cloneNode(true)
      const verticalOffset = this.config.verticalOffset || 0
      const horizontalOffset = this.config.horizontalOffset || 0
      decoratorContainer.style.top = `${top + verticalOffset}px`
      decoratorContainer.style.left = `${left + horizontalOffset}px`
      decoratorContainer.style.width = `${width}px`
      decoratorContainer.style.height = `${height}px`

      const transforms = []
      if (this.config.flipHorizontal) transforms.push('scaleX(-1)')
      if (this.config.flipVertical) transforms.push('scaleY(-1)')
      if (transforms.length) {
        decoratorContainer.style.transform = transforms.join(' ')
      }

      decoratorContainer.insertAdjacentElement(
        'beforeend',
        this.drawDecorator(rect)
      )

      this.decoratorsContainer.insertAdjacentElement(
        'beforeend',
        decoratorContainer
      )
    })
  })

  computeDecoratorPositions() {
    const referenceBBox = this.referenceElement?.getClientRects?.()[0]
    if (!referenceBBox) return []

    const bottomOffsetRatio = this.useAscenderHeight()
      ? calculateAscenderHeightRatio(this.element)
      : 0

    const rects = this.element.getClientRects()

    return Array.from(rects)
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .map((rect) => ({
        top: rect.top - referenceBBox.top,
        left: rect.left - referenceBBox.left,
        width: rect.width,
        height: rect.height - bottomOffsetRatio * rect.height,
      }))
  }

  setupAnimation = () => {
    if (this.tiptapEditor || this.animationStartedAlready) {
      this.decoratorsContainer.classList.add('animation-started')
    }

    this.decoratorsContainer.classList.toggle(
      'decorator-animated',
      this.config.animation
    )

    if (this.config.animation) {
      this.decoratorsContainer.classList.toggle(
        `decorator-${this.config.animationType}-animation`,
        this.config.animationType
      )
      this.decoratorsContainer.style.setProperty(
        '--animation-duration',
        `${this.config.animationDuration}s`
      )
      this.decoratorsContainer.dataset.controller = 'intersection-observer'
      this.decoratorsContainer.dataset.intersectionObserverOnlyOnceValue = true
      this.decoratorsContainer.addEventListener('appear', this.startAnimation, {
        once: true,
      })
    }
  }

  startAnimation = debounce(10, () => {
    requestAnimationFrame(() => {
      this.animationStartedAlready = true
      this.decoratorsContainer.classList.add('animation-started')
    })
  })

  restartAnimation = () => {
    this.decoratorsContainer.classList.remove(
      'animation-started',
      'decorator-animated'
    )
    void this.decoratorsContainer.offsetWidth
    this.decoratorsContainer.classList.add('decorator-animated')
    this.startAnimation()
  }

  initializeReferenceElement() {
    /*
     * When rendering these on a tiptap editor, we position them relative to the editor container.
     * This is to make it so that tiptap does not interfere with the elements inserted by this controller.
     * We've tried a lot of different things to make this work with TipTap normally, but it was seemingly not possible (at least in a simple/maintainable way).
     * So, if we are in a tiptap editor use that as the reference
     * (except if we are in a dropdown, which is in a tiptap editor, ignore that special case, its coming from the decoration dropdown component)
     */

    if (this.tiptapEditor) {
      this.referenceElement = this.tiptapEditor
    } else {
      this.referenceElement = this.element
    }

    this.referenceElement.classList.add('text-decoration-reference-element')
  }

  get tiptapEditor() {
    if (this.__tiptapEditor === undefined) {
      const closestDropdown = this.element.closest('.js-dropdown')
      const closestTiptapEditor = this.element.closest(
        '.js-tiptap-editor-container'
      )

      this.__tiptapEditor =
        closestTiptapEditor &&
        (!closestDropdown || !closestTiptapEditor.contains(closestDropdown))
          ? closestTiptapEditor
          : null
    }

    return this.__tiptapEditor
  }

  get builderNode() {
    return this.element.closest('.builder-node')
  }

  getClosestNonInlineAncestor(el) {
    while (el && el.tagName !== 'BODY') {
      if (getComputedStyle(el).display !== 'inline') {
        break
      }
      el = el.parentElement
    }
    return el
  }

  /* Helpers used by sub classes */
  createSvg(width, height) {
    const s = document.createElementNS(SVG_NS, 'svg')
    s.setAttribute('viewBox', `0 0 ${width} ${height}`)
    s.style.display = 'block'
    s.style.width = `${width}px`
    s.style.height = `${height}px`
    s.style.maxWidth = 'none'
    s.style.maxHeight = 'none'
    return s
  }

  createPath(d) {
    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('d', d)
    return path
  }

  createRect(width, height) {
    const rect = document.createElementNS(SVG_NS, 'rect')
    rect.setAttribute('width', width)
    rect.setAttribute('height', height)
    rect.setAttribute('x', 0)
    rect.setAttribute('y', 0)
    return rect
  }
}

export function getAnimationConfig(config) {
  return {
    style: config.style,
    animation: config.animation,
    animationType: config.animationType,
    animationDuration: config.animationDuration,
  }
}

export function getStrokeWidthPixel(element, strokeWidth, strokeWidthUnit) {
  switch (strokeWidthUnit) {
    case 'px':
      return parseFloat(strokeWidth)
    case 'em': {
      const computedStyle = getComputedStyle(element)
      const fontSize = parseFloat(computedStyle.fontSize) || 16
      return fontSize * parseFloat(strokeWidth)
    }
    default:
      return parseFloat(strokeWidth) || 2
  }
}
